import React, { useRef, useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProtectedNavLink } from "@/components/ProtectedNavigationLink";
import { RouteId } from "@/routes/getRoute";

export interface FlatTabItem {
  label: string;
  active: boolean;
  completed?: boolean;
  number?: number;
  icon?: React.ReactNode;
  route?: RouteId;
  params?: Record<string, string>;

  onClick?: () => void;
}

interface FlatTabProps {
  items: FlatTabItem[];
  className?: string;
}

/**
 * FlatTab - A reusable horizontal tab navigation component with circles, icons, and underlines
 *
 * FEATURES:
 * - Numbered circles with consistent background color (bg-muted)
 * - Custom icons that replace circles
 * - Active tab with thick underline that animates
 * - Completed state support with checkmark icons
 * - Clickable tabs with navigation or click handlers
 * - Smooth transitions between tabs
 * - Responsive opacity states for all elements
 * - Theme-aware styling using CSS variables
 * - **Protected routing support with permission checking**
 *
 * STYLING DETAILS:
 * - Circle background: Uses bg-muted (constant color, theme-aware)
 * - Number text: Active 100% opacity, Inactive 30% opacity
 * - Label text: Active 100% opacity, Inactive 40% opacity
 * - Icons: Active 100% opacity, Inactive 20% opacity
 * - All text uses text-foreground for theme consistency
 *
 * USAGE EXAMPLES:
 *
 * // Protected routes with permission checking
 * <FlatTab
 *   items={[
 *     { label: "Choose Type", active: false, completed: true, number: 1, route: "editEvent" },
 *     { label: "Configure", active: true, number: 2, route: "editLiveEvent" },
 *     { label: "Review", active: false, number: 3, route: "nowhere" }
 *   ]}
 * />
 *
 * // With route parameters
 * <FlatTab
 *   items={[
 *     {
 *       label: "Student Profile",
 *       active: true,
 *       icon: <User />,
 *       route: "student",
 *       params: { studentId: "123" }
 *     },
 *   ]}
 * />
 *
 * // Click handlers for non-navigation tabs
 * <FlatTab
 *   items={[
 *     { label: "Overview", active: true, number: 1, route: "communityTools" },
 *     { label: "Details", active: false, icon: <CheckCircle />, onClick: handleDetailsClick }
 *   ]}
 * />
 *
 * ROUTING NOTES:
 * - All navigation uses RouteId for type safety and permission checking
 * - Use `route="nowhere"` for tabs that shouldn't navigate anywhere
 *
 * LAYOUT NOTES:
 * - Component has no horizontal padding - container handles layout
 * - No bottom border - parent component should provide page borders
 * - Uses gap-8 between tabs for proper spacing
 * - 17px bottom padding for underline positioning
 *
 * @param items - Array of tab configuration objects
 * @param className - Additional CSS classes for container
 */
export const FlatTab = ({ items, className }: FlatTabProps) => {
  const tabRefs = useRef<(HTMLElement | null)[]>([]);
  const [underlineStyle, setUnderlineStyle] = useState<{
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, items.length);
  }, [items.length]);

  useEffect(() => {
    const activeIndex = items.findIndex((item) => item.active);
    if (activeIndex !== -1) {
      const activeTab = tabRefs.current[activeIndex];
      if (activeTab) {
        const { offsetLeft, offsetWidth } = activeTab;
        setUnderlineStyle({
          left: offsetLeft,
          width: offsetWidth,
        });
      }
    } else {
      setUnderlineStyle(null);
    }
  }, [items]);

  const renderTabContent = (item: FlatTabItem) => {
    const hasCircle = item.number !== undefined || item.completed || item.icon;

    if (hasCircle) {
      return (
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded-full bg-muted",
              item.active ? "opacity-100" : "opacity-50",
            )}
          >
            <span
              className={cn(
                "text-xs font-medium text-foreground",
                item.active ? "opacity-100" : "opacity-30",
              )}
            >
              {item.completed
                ? item.icon || <CheckCircle className="h-5 w-5" />
                : item.icon || item.number}
            </span>
          </div>
          <span
            className={cn(
              "text-sm font-normal text-foreground",
              item.active ? "font-medium opacity-100" : "text-slate-300",
            )}
          >
            {item.label}
          </span>
        </div>
      );
    }

    return (
      <span
        className={cn(
          "text-sm font-normal text-foreground",
          item.active ? "font-medium opacity-100" : "opacity-40",
        )}
      >
        {item.label}
      </span>
    );
  };

  const renderTab = (item: FlatTabItem, index: number) => {
    const content = renderTabContent(item);
    const commonProps = {
      ref: (el: HTMLElement | null) => {
        tabRefs.current[index] = el;
      },
      className: cn(
        "flex items-center gap-2",
        (item.route || item.onClick) &&
          "cursor-pointer hover:opacity-80 transition-opacity",
      ),
    };

    if (item.onClick) {
      return (
        <button
          key={index}
          onClick={item.onClick}
          aria-current={item.active ? "page" : false}
          {...commonProps}
        >
          {content}
        </button>
      );
    }

    if (item.route) {
      return (
        <ProtectedNavLink
          key={index}
          route={item.route}
          params={item.params}
          {...commonProps}
        >
          {content}
        </ProtectedNavLink>
      );
    }

    return (
      <div key={index} {...commonProps}>
        {content}
      </div>
    );
  };

  return (
    <div className={cn("relative w-full", className)}>
      <div
        className="flex items-center gap-8"
        style={{ paddingBottom: "17px" }}
        role="tablist"
      >
        {items.map((item, index) => renderTab(item, index))}
      </div>

      {underlineStyle && (
        <div
          className="absolute bottom-0 h-1 bg-foreground transition-all duration-200 ease-in-out"
          style={{
            left: `${underlineStyle.left}px`,
            width: `${underlineStyle.width}px`,
          }}
        />
      )}
    </div>
  );
};
